

// Day arithmetic goes through the local Date constructor so it stays correct
// across daylight-saving changes.

const pad = (n: number) => String(n).padStart(2, "0");

const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

// Graph sends up to 7 fractional digits ("2026-09-28T09:00:00.0000000"), which
// `new Date()` only accepts through engine leniency — so parse by hand.
const GRAPH_DATE_TIME_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,7}))?)?Z?$/;
const DAY_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A Graph `dateTime` requested in UTC → instant, or null when unparseable. */
export function parseGraphUtc(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = GRAPH_DATE_TIME_RE.exec(value.trim());
  if (!m) return null;
  const fraction = m[7] ?? "";
  const ms = fraction ? Number(`${fraction}00`.slice(0, 3)) : 0;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] ?? "0"), ms);
  return Number.isFinite(t) ? new Date(t) : null;
}

export function dayKeyOf(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The local day an all-day event's UTC midnight belongs to. Graph stores it as
 * midnight in the organiser's zone, so rounding to the nearest local midnight
 * keeps it on the right date for offsets under 12 hours.
 */
export function nearestLocalDay(instant: Date): string {
  return dayKeyOf(new Date(instant.getTime() + 12 * 60 * 60 * 1000));
}

function dayParts(k: string): [number, number, number] {
  const m = DAY_KEY_RE.exec(k);
  if (!m) return [1970, 0, 1];
  return [Number(m[1]), Number(m[2]) - 1, Number(m[3])];
}

/** Local midnight at the start of `k`. */
export function dayStart(k: string): Date {
  const [y, mo, d] = dayParts(k);
  return new Date(y, mo, d);
}

/** Local wall-clock `min` minutes into day `k`. */
export function atDayMinutes(k: string, min: number): Date {
  const [y, mo, d] = dayParts(k);
  return new Date(y, mo, d, 0, min);
}

/** "11:30–12:00" for two instants. */
export function rangeLabel(start: Date, end: Date): string {
  return `${hm(minutesOfDay(start))}–${hm(minutesOfDay(end))}`;
}

export function addDays(k: string, n: number): string {
  const [y, mo, d] = dayParts(k);
  return dayKeyOf(new Date(y, mo, d + n));
}

export function startOfWeekMonday(k: string): string {
  return addDays(k, -((dayStart(k).getDay() + 6) % 7));
}

/** Wall-clock minutes since local midnight. */
export function minutesOfDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

/** Minutes of day → "09:05". */
export function hm(min: number): string {
  return `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;
}

/** Duration in the mockup's format: "45 min", "1h", "1h 30min". */
export function fd(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const rest = min % 60;
  return rest ? `${h}h ${rest}min` : `${h}h`;
}

/** "Mon 3 Aug". */
export function dayLabel(k: string): string {
  const d = dayStart(k);
  return `${DAY_SHORT[d.getDay()] ?? ""} ${d.getDate()} ${MONTH_SHORT[d.getMonth()] ?? ""}`;
}

/** "Today · Mon 3 Aug" for today, otherwise "Tue 4 Aug". */
export function dayName(k: string, today: string): string {
  return k === today ? `Today · ${dayLabel(k)}` : dayLabel(k);
}

/** "Mon 3 – Fri 7 Aug", or "Mon 29 Sep – Fri 3 Oct" across a month boundary. */
export function weekLabel(mon: string): string {
  const a = dayStart(mon);
  const b = dayStart(addDays(mon, 4));
  const monthB = MONTH_SHORT[b.getMonth()] ?? "";
  return a.getMonth() === b.getMonth()
    ? `Mon ${a.getDate()} – Fri ${b.getDate()} ${monthB}`
    : `Mon ${a.getDate()} ${MONTH_SHORT[a.getMonth()] ?? ""} – Fri ${b.getDate()} ${monthB}`;
}

/** "Week of 3 Aug". */
export function weekOfLabel(mon: string): string {
  const a = dayStart(mon);
  return `Week of ${a.getDate()} ${MONTH_SHORT[a.getMonth()] ?? ""}`;
}
